<template>

    <div class="row">
        <div class="col">
            <kt-input 
                v-model="model.Company" 
                label="Company Name" 
                placeholder="Enter company name"
            ></kt-input>
        </div>
    </div>

    <div class="row mt-2">
        <div class="col">
            <kt-input 
                v-model="model.Code" 
                label="Company Code" 
                placeholder="Enter company code"
            ></kt-input>
        </div>
    </div>

    <div class="row mt-2">
        <div class="col">
            <kt-input 
                type="textarea" 
                v-model="model.ContactInformation" 
                label="Contact Information" 
                placeholder="Enter company contact information"
            ></kt-input>
        </div>
    </div>

    <div class="row mt-2">
        <div class="col">
            <kt-input 
                type="text" 
                v-model="model.Phone" 
                label="Main Phone" 
                placeholder="Enter company phone"
            ></kt-input>
        </div>
    </div>

  <div class="row mt-2">
    <div class="col">
      <div class="form-check">
        <input
            type="checkbox"
            class="form-check-input"
            v-model="model.Status"
            :true-value="1"
            :false-value="0"
            id="active-check"
        >
        <label 
            class="form-check-label" 
            for="active-check">
          Is Active?
        </label>
      </div>
    </div>
  </div>

  <!-- Profile Image Section -->
  <div class="row mt-4" v-if="model.Id">
    <div class="col">
      <h6 class="text-muted mb-2">Profile Image</h6>
      
      <!-- Display existing image -->
      <div v-if="profileImage" class="mb-3">
        <img 
          :src="imageUrl" 
          alt="Profile Image"
          class="img-thumbnail"
          style="max-width: 200px; max-height: 200px; object-fit: cover;"
        />
        <div class="mt-2">
          <button 
            @click="deleteImage()" 
            class="btn btn-sm btn-outline-danger me-2"
          >
            <i class="fa fa-trash"></i> Delete Image
          </button>
          <label class="btn btn-sm btn-outline-primary">
            <i class="fa fa-upload"></i> Replace Image
            <input 
              type="file" 
              @change="handleImageUpload($event)" 
              accept="image/*"
              style="display: none;"
            />
          </label>
        </div>
      </div>

      <!-- Upload new image -->
      <div v-else>
        <label class="btn btn-sm btn-outline-primary">
          <i class="fa fa-upload"></i> Upload Profile Image
          <input 
            type="file" 
            @change="handleImageUpload($event)" 
            accept="image/*"
            style="display: none;"
          />
        </label>
        <p class="text-muted small mt-2">Supported formats: JPG, PNG, GIF (max 5MB)</p>
      </div>
    </div>
  </div>
    
</template>

<script setup>
import { ref, onMounted, watch, computed } from "vue";
import { useRoute } from "vue-router"
import Api from "@/services/Api";
import ApiService from "@/services/ApiService";
import Alert from "@/services/Alert";
import KtInput from "@/components/UI/KtInput.vue";
import { getApiBaseUrl } from "@/services/Http";

const route = useRoute();

const model = defineModel({
    type: Object,
    default: {}
});

const profileImage = ref(null);
const imageUrl = computed(() => {
    return profileImage.value ? `${getApiBaseUrl()}/Data/Download/${profileImage.value.Id}` : '';
});

// Load profile image when model.Id changes
watch(() => model.value.Id, async (newId) => {
    if (newId) {
        await loadProfileImage();
    }
}, { immediate: true });

const loadProfileImage = async () => {
    if (!model.value.Id) return;
    
    try {
        const response = await ApiService.loadContactImage(model.value.Id);
        profileImage.value = response || null;
    } catch (error) {
        console.error('Error loading profile image:', error);
        profileImage.value = null;
    }
};

const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
        Alert.notify({
            text: 'Image size must be less than 5MB',
            type: 'error'
        });
        return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
        Alert.notify({
            text: 'Please select a valid image file',
            type: 'error'
        });
        return;
    }

    try {
        const response = await ApiService.uploadProfileImage(file, 'contact', model.value.Id);
        profileImage.value = response;
        Alert.notify({
            text: 'Profile image uploaded successfully!',
            type: 'success'
        });
    } catch (error) {
        console.error('Error uploading image:', error);
        Alert.notify({
            text: 'Error uploading image',
            type: 'error'
        });
    }

    // Reset file input
    event.target.value = '';
};

const deleteImage = async () => {
    if (!profileImage.value) return;

    Alert.confirm('Are you sure you want to delete this profile image?', async () => {
        try {
            await ApiService.deleteProfileImage(profileImage.value.Id);
            profileImage.value = null;
            Alert.notify({
                text: 'Profile image deleted successfully!',
                type: 'success'
            });
        } catch (error) {
            console.error('Error deleting image:', error);
            Alert.notify({
                text: 'Error deleting image',
                type: 'error'
            });
        }
    });
};

onMounted(async () =>
{
    /*
    if (route.params.id === "new")
    {
        return;
    }
    
    model.value = await Api.loadClient(route.params.id);
    */
});

</script>